import { ContactList } from "components/ui/ContactList";
import { Button } from "components/ui/Button";
import { Modal } from "components/ui/Modal";
import { Trash2, Users } from "lucide-react";
import { useResidents } from "context/ResidentsContext";
import { useCondos } from "context/CondosContext";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "context/AuthContext";
import fetchWithCredentials from "apiClient";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import {
  emptyUnitListSubtitle,
  residentListSubtitle,
} from "utils/contactListSubtitle";
import { ReplyMuteBadge } from "components/ReplyMuteBadge";
import type { ReplyMute } from "types/ReplyMute";

interface ResidentsListProps {
  onSelectContact: (contactId: number) => void;
  selectedContactId: number | null;
  condoId: number;
}

type ResidentRow = {
  id: number;
  contactId: number;
  name: string;
  subtitle: string;
  lastMessageAt: string | null;
  hasUnread: boolean;
  isActive: boolean;
  rowKind: "resident" | "empty_unit";
  replyMute?: ReplyMute | null;
};

type UnitPendingRemoval = {
  id: number;
  name: string;
};

export function ResidentsList({
  onSelectContact,
  selectedContactId,
  condoId,
}: ResidentsListProps) {
  const { residents, emptyUnits, loading, error, refresh, markContactAsRead } =
    useResidents();
  const { refresh: refreshCondos } = useCondos();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [unitPendingRemoval, setUnitPendingRemoval] =
    useState<UnitPendingRemoval | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleSelectContact = (contactId: number) => {
    markContactAsRead(contactId);
    onSelectContact(contactId);
  };

  // Fetch residents when condo changes
  useEffect(() => {
    refresh(condoId);
  }, [condoId, refresh]);

  const listItems: ResidentRow[] = useMemo(() => {
    const residentRows = residents.map((resident) => ({
      id: resident.id,
      contactId: resident.contact.id,
      name: resident.contact.name,
      subtitle: residentListSubtitle(resident, locale),
      lastMessageAt: resident.lastMessageAt,
      hasUnread: resident.hasUnread,
      isActive: resident.isActive ?? true,
      rowKind: "resident" as const,
      replyMute: resident.replyMute ?? null,
    }));
    const emptyUnitRows = emptyUnits.map((unit) => ({
      id: unit.id,
      contactId: -unit.id,
      name: unit.name,
      subtitle: emptyUnitListSubtitle(locale),
      lastMessageAt: null,
      replyMute: null,
      hasUnread: false,
      isActive: true,
      rowKind: "empty_unit" as const,
    }));
    return [...residentRows, ...emptyUnitRows];
  }, [residents, emptyUnits, locale]);

  const emptyUnitsLabel =
    locale === "es" ? "Unidades sin residente" : "Units without residents";
  const removeUnitLabel =
    locale === "es" ? "Eliminar unidad" : "Remove unit";

  const closeRemoveModal = () => {
    if (removing) return;
    setUnitPendingRemoval(null);
    setRemoveError(null);
  };

  const confirmRemoveEmptyUnit = async () => {
    if (!unitPendingRemoval || removing) return;

    setRemoving(true);
    setRemoveError(null);
    try {
      await fetchWithCredentials(
        `/manager-api/units/${unitPendingRemoval.id}/deactivate/?condo=${condoId}`,
        { method: "POST" }
      );
      await Promise.all([refresh(condoId), refreshCondos()]);
      setUnitPendingRemoval(null);
    } catch (err) {
      console.error(err);
      setRemoveError(
        err instanceof Error
          ? err.message
          : locale === "es"
            ? "No se pudo eliminar la unidad."
            : "Could not remove the unit."
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <ContactList<ResidentRow>
        listId="residents"
        title={locale === "es" ? "Residentes" : "Residents"}
        icon={<Users className="w-4 h-4 mr-2" />}
        items={listItems}
        loading={loading}
        error={error}
        searchPlaceholder={
          emptyUnits.length > 0
            ? locale === "es"
              ? "Buscar residentes o unidades..."
              : "Search residents or units..."
            : locale === "es"
              ? "Buscar residentes..."
              : "Search residents..."
        }
        selectedId={selectedContactId}
        onSelectItem={handleSelectContact}
        getItemName={(item) => `${item.name} ${item.subtitle}`.trim()}
        getItemKey={(item) => `${item.rowKind}-${item.id}`}
        getSelectionId={(item) => item.contactId}
        isItemSelectable={(item) => item.rowKind === "resident"}
        countItemsForHeader={(item) =>
          item.rowKind === "resident" && item.isActive
        }
        renderItem={(item, isSelected, { filteredItems, filteredIndex }) => {
          const isEmptyUnit = item.rowKind === "empty_unit";
          const isFirstEmptyUnit =
            isEmptyUnit &&
            !filteredItems
              .slice(0, filteredIndex)
              .some((row) => row.rowKind === "empty_unit");
          return (
            <div>
              {isFirstEmptyUnit && (
                <div className="px-2 pb-1 pt-2 text-xs font-medium text-gray-500">
                  {emptyUnitsLabel}
                </div>
              )}
              <div
                className={contactListItemClasses(isSelected, {
                  muted: isEmptyUnit,
                  inactive: item.rowKind === "resident" && !item.isActive,
                  clickable: !isEmptyUnit,
                })}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-xs lg:text-sm truncate ${
                        item.hasUnread ? "font-bold" : "font-medium"
                      } ${isEmptyUnit || !item.isActive ? "text-gray-500" : ""}`}
                    >
                      {item.name}
                    </div>
                    <div className="text-xs text-gray-500">{item.subtitle}</div>
                    <ReplyMuteBadge mute={item.replyMute} locale={locale} />
                  </div>
                  {isEmptyUnit ? (
                    <button
                      type="button"
                      className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                      title={removeUnitLabel}
                      aria-label={`${removeUnitLabel} ${item.name}`}
                      disabled={removing}
                      onClick={(event) => {
                        event.stopPropagation();
                        setRemoveError(null);
                        setUnitPendingRemoval({ id: item.id, name: item.name });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    item.lastMessageAt && (
                      <div className="text-xs text-gray-400 flex-shrink-0">
                        {formatTimestamp(item.lastMessageAt, locale)}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        }}
      />

      <Modal
        isOpen={unitPendingRemoval != null}
        onClose={closeRemoveModal}
        title={removeUnitLabel}
      >
        <p className="text-sm text-gray-700">
          {locale === "es"
            ? `¿Eliminar la unidad ${unitPendingRemoval?.name ?? ""}?`
            : `Remove unit ${unitPendingRemoval?.name ?? ""}?`}
        </p>
        {removeError && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {removeError}
          </p>
        )}
        <div className="mt-6 flex gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1"
            disabled={removing}
            onClick={closeRemoveModal}
          >
            {locale === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1 bg-red-600 text-white hover:bg-red-700 border-red-600"
            disabled={removing}
            onClick={() => {
              void confirmRemoveEmptyUnit();
            }}
          >
            {removing
              ? locale === "es"
                ? "Eliminando..."
                : "Removing..."
              : locale === "es"
                ? "Eliminar"
                : "Remove"}
          </Button>
        </div>
      </Modal>
    </>
  );
}
